import {
    CategoryElement,
    DateElement,
    Element,
    ELEMENT_TYPE_CATEGORY,
    ELEMENT_TYPE_DATE,
    ELEMENT_TYPE_FILE,
    ELEMENT_TYPE_FORMATTED_TEXT,
    ELEMENT_TYPE_GROUP,
    ELEMENT_TYPE_IMAGE,
    ELEMENT_TYPE_LINK,
    ELEMENT_TYPE_LOCATION,
    ELEMENT_TYPE_NUMBER,
    ELEMENT_TYPE_OPTION_SELECTION,
    ELEMENT_TYPE_REFERENCE,
    ELEMENT_TYPE_TEXT,
    ELEMENT_TYPE_TOGGLE,
    ELEMENT_TYPE_VIDEO,
    ElementMap,
    File,
    FileElement,
    FormattedTextElement,
    Group,
    GroupElement,
    Image,
    ImageElement,
    Link,
    LinkElement,
    LocationElement,
    MultiDateElement,
    MultiFileElement,
    MultiFormattedTextElement,
    MultiGroupElement,
    MultiImageElement,
    MultiLinkElement,
    MultiNumberElement,
    MultiOptionSelectionElement,
    MultiReferenceElement,
    MultiTextElement,
    MultiToggleElement,
    MultiVideoElement,
    NumberElement,
    OptionSelection,
    OptionSelectionElement,
    ReferenceElement,
    SingleDateElement,
    SingleFileElement,
    SingleFormattedTextElement,
    SingleGroupElement,
    SingleImageElement,
    SingleLinkElement,
    SingleNumberElement,
    SingleOptionSelectionElement,
    SingleReferenceElement,
    SingleTextElement,
    SingleToggleElement,
    SingleVideoElement,
    TextElement,
    ToggleElement,
    TypeRef,
    Video,
    VideoElement,
} from './../../api';
import {
    isArray,
    isArrayOf,
    isBoolean,
    isNotNil,
    isNumber,
    isObjectOf,
    isPlainObject,
    isString,
    Predicate,
} from './../predicates/predicates';

/* Copyright IBM Corp. 2017 */
const _PROP_TYPE = 'elementType';
const _PROP_VALUE = 'value';
const _PROP_VALUES = 'values';
const _PROP_SELECTION = 'selection';
const _PROP_TYPE_REF = 'typeRef';
const _PROP_ID = 'id';

function _isType(aType: string, aValue: any | null | undefined): boolean {
    return isNotNil(aValue) && (aValue[_PROP_TYPE] === aType);
}

function _isTypeRef(aValue: any): aValue is TypeRef {
    return isNotNil(aValue) && isPlainObject(aValue) && isString(aValue[_PROP_ID]);
}

/*
 * Tests if an element is an option selection element
 *
 * @param aValue  the value to test
 * @return the type assertion
 */
function _isOptionSelectionElement(aValue: any): aValue is OptionSelectionElement {
    return _isType(ELEMENT_TYPE_OPTION_SELECTION, aValue);
}

function _isOptionSelection(aValue: any): aValue is OptionSelection {
    return isPlainObject(aValue) && isString(aValue[_PROP_SELECTION]);
}

/*
 * Tests if an element is an option selection element
 *
 * @param aValue  the value to test
 * @return the type assertion
 */
function _isSingleOptionSelectionElement(aValue: any): aValue is SingleOptionSelectionElement {
    return _isOptionSelectionElement(aValue) && _isOptionSelection(aValue[_PROP_VALUE]);
}

/*
 * Tests if an element is an option selection element
 *
 * @param aValue  the value to test
 * @return the type assertion
 */
function _isMultiOptionSelectionElement(aValue: any): aValue is MultiOptionSelectionElement {
    return _isOptionSelectionElement(aValue) && _isOptionSelection(aValue[_PROP_VALUES]);
}

/*
 * Tests if an element is a text element
 *
 * @param aValue  the value to test
 * @return the type assertion
 */
function _isTextElement(aValue: any): aValue is TextElement {
    return _isType(ELEMENT_TYPE_TEXT, aValue);
}

/*
 * Tests if an element is a text element
 *
 * @param aValue  the value to test
 * @return the type assertion
 */
function _isSingleTextElement(aValue: any): aValue is SingleTextElement {
    return _isTextElement(aValue) && isString(aValue[_PROP_VALUE]);
}

/*
 * Tests if an element is a text element
 *
 * @param aValue  the value to test
 * @return the type assertion
 */
function _isMultiTextElement(aValue: any): aValue is MultiTextElement {
    return _isTextElement(aValue) && isArrayOf(aValue[_PROP_VALUES], isString);
}

/*
 * Tests if an element is a text element
 *
 * @param aValue  the value to test
 * @return the type assertion
 */
function _isSingleDateElement(aValue: any): aValue is SingleDateElement {
    return _isDateElement(aValue) && isString(aValue[_PROP_VALUE]);
}

/*
 * Tests if an element is a text element
 *
 * @param aValue  the value to test
 * @return the type assertion
 */
function _isMultiDateElement(aValue: any): aValue is MultiDateElement {
    return _isDateElement(aValue) && isArrayOf(aValue[_PROP_VALUES], isString);
}

/*
 * Tests if an element is a Group element
 *
 * @param aValue  the value to test
 * @return the type assertion
 */
function _isGroupElement(aValue: any): aValue is GroupElement {
    return _isType(ELEMENT_TYPE_GROUP, aValue) && _isTypeRef(aValue[_PROP_TYPE_REF]);
}

/**
 * Tests if the value is a vaid group
 *
 * @param aValue the value
 * @return true if the value is a group, else false
 */
function _isGroup(aValue: any): aValue is Group {
    return isObjectOf(aValue, _isElement);
}

/*
 * Tests if an element is a Group element
 *
 * @param aValue  the value to test
 * @return the type assertion
 */
function _isSingleGroupElement(aValue: any): aValue is SingleGroupElement {
    return _isGroupElement(aValue) && _isGroup(aValue[_PROP_VALUE]);
}

/*
 * Tests if an element is a Group element
 *
 * @param aValue  the value to test
 * @return the type assertion
 */
function _isMultiGroupElement(aValue: any): aValue is MultiGroupElement {
    return _isGroupElement(aValue) && isArrayOf(aValue[_PROP_VALUES], _isGroup);
}

/*
 * Tests if an element is a text element
 *
 * @param aValue  the value to test
 * @return the type assertion
 */
function _isFormattedTextElement(aValue: any): aValue is FormattedTextElement {
    return _isType(ELEMENT_TYPE_FORMATTED_TEXT, aValue);
}

/*
 * Tests if an element is a text element
 *
 * @param aValue  the value to test
 * @return the type assertion
 */
function _isSingleFormattedTextElement(aValue: any): aValue is SingleFormattedTextElement {
    return _isFormattedTextElement(aValue) && isString(aValue[_PROP_VALUE]);
}

/*
 * Tests if an element is a text element
 *
 * @param aValue  the value to test
 * @return the type assertion
 */
function _isMultiFormattedTextElement(aValue: any): aValue is MultiFormattedTextElement {
    return _isFormattedTextElement(aValue) && isArrayOf(aValue[_PROP_VALUES], isString);
}

/*
 * Tests if an element is a number element
 *
 * @param aValue  the value to test
 * @return the type assertion
 */
function _isNumberElement(aValue: any): aValue is NumberElement {
    return _isType(ELEMENT_TYPE_NUMBER, aValue);
}

/*
 * Tests if an element is a number element
 *
 * @param aValue  the value to test
 * @return the type assertion
 */
function _isSingleNumberElement(aValue: any): aValue is SingleNumberElement {
    return _isNumberElement(aValue) && isNumber(aValue[_PROP_VALUE]);
}

/*
 * Tests if an element is a number element
 *
 * @param aValue  the value to test
 * @return the type assertion
 */
function _isMultiNumberElement(aValue: any): aValue is MultiNumberElement {
    return _isNumberElement(aValue) && isArrayOf(aValue[_PROP_VALUES], isNumber);
}

/*
 * Tests if an element is a toggle element
 *
 * @param aValue  the value to test
 * @return the type assertion
 */
function _isToggleElement(aValue: any): aValue is ToggleElement {
    return _isType(ELEMENT_TYPE_TOGGLE, aValue);
}

/*
 * Tests if an element is a toggle element
 *
 * @param aValue  the value to test
 * @return the type assertion
 */
function _isSingleToggleElement(aValue: any): aValue is SingleToggleElement {
    return _isToggleElement(aValue) && isBoolean(aValue[_PROP_VALUE]);
}

/*
 * Tests if an element is a toggle element
 *
 * @param aValue  the value to test
 * @return the type assertion
 */
function _isMultiToggleElement(aValue: any): aValue is MultiToggleElement {
    return _isToggleElement(aValue) && isArrayOf(aValue[_PROP_VALUES], isBoolean);
}

/*
 * Tests if an element is a link element
 *
 * @param aValue  the value to test
 * @return the type assertion
 */
function _isLinkElement(aValue: any): aValue is LinkElement {
    return _isType(ELEMENT_TYPE_LINK, aValue);
}

/*
 * Tests if an element is a date element
 *
 * @param aValue  the value to test
 * @return the type assertion
 */
function _isDateElement(aValue: any): aValue is DateElement {
    return _isType(ELEMENT_TYPE_DATE, aValue);
}

/*
 * Tests if an element is a category element
 *
 * @param aValue  the value to test
 * @return the type assertion
 */
function _isCategoryElement(aValue: any): aValue is CategoryElement {
    return _isType(ELEMENT_TYPE_CATEGORY, aValue);
}

/*
 * Tests if an element is a file element
 *
 * @param aValue  the value to test
 * @return the type assertion
 */
function _isFileElement(aValue: any): aValue is FileElement {
    return _isType(ELEMENT_TYPE_FILE, aValue);
}

function _isFile(aValue: any): aValue is File {
    return isPlainObject(aValue);
}

function _isVideo(aValue: any): aValue is Video {
    return isPlainObject(aValue);
}

function _isLink(aValue: any): aValue is Link {
    return isPlainObject(aValue);
}

function _isImage(aValue: any): aValue is Image {
    return isPlainObject(aValue);
}

/*
 * Tests if an element is a file element
 *
 * @param aValue  the value to test
 * @return the type assertion
 */
function _isSingleFileElement(aValue: any): aValue is SingleFileElement {
    return _isFileElement(aValue) && _isFile(aValue);
}

/*
 * Tests if an element is a file element
 *
 * @param aValue  the value to test
 * @return the type assertion
 */
function _isMultiFileElement(aValue: any): aValue is MultiFileElement {
    return _isFileElement(aValue) && _isFile(aValue[_PROP_VALUES]);
}

/*
 * Tests if an element is a video element
 *
 * @param aValue  the value to test
 * @return the type assertion
 */
function _isVideoElement(aValue: any): aValue is VideoElement {
    return _isType(ELEMENT_TYPE_VIDEO, aValue);
}

/*
 * Tests if an element is an image element
 *
 * @param aValue  the value to test
 * @return the type assertion
 */
function _isImageElement(aValue: any): aValue is ImageElement {
    return _isType(ELEMENT_TYPE_IMAGE, aValue);
}

/*
 * Tests if an element is an image element
 *
 * @param aValue  the value to test
 * @return the type assertion
 */
function _isSingleImageElement(aValue: any): aValue is SingleImageElement {
    return _isImageElement(aValue) && _isImage(aValue);
}

/*
 * Tests if an element is an image element
 *
 * @param aValue  the value to test
 * @return the type assertion
 */
function _isMultiImageElement(aValue: any): aValue is MultiImageElement {
    return _isImageElement(aValue) && _isImage(aValue[_PROP_VALUES]);
}

/*
 * Tests if an element is an image element
 *
 * @param aValue  the value to test
 * @return the type assertion
 */
function _isSingleVideoElement(aValue: any): aValue is SingleVideoElement {
    return _isVideoElement(aValue) && _isVideo(aValue);
}

/*
 * Tests if an element is an image element
 *
 * @param aValue  the value to test
 * @return the type assertion
 */
function _isMultiVideoElement(aValue: any): aValue is MultiVideoElement {
    return _isVideoElement(aValue) && _isVideo(aValue[_PROP_VALUES]);
}

/*
 * Tests if an element is an image element
 *
 * @param aValue  the value to test
 * @return the type assertion
 */
function _isSingleLinkElement(aValue: any): aValue is SingleLinkElement {
    return _isLinkElement(aValue) && _isLink(aValue);
}

/*
 * Tests if an element is an image element
 *
 * @param aValue  the value to test
 * @return the type assertion
 */
function _isMultiLinkElement(aValue: any): aValue is MultiLinkElement {
    return _isLinkElement(aValue) && _isLink(aValue[_PROP_VALUES]);
}

/*
 * Tests if an element is a reference element
 *
 * @param aValue  the value to test
 * @return the type assertion
 */
function _isReferenceElement(aValue: any): aValue is ReferenceElement {
    return _isType(ELEMENT_TYPE_REFERENCE, aValue);
}

/*
 * Tests if an element is a reference element
 *
 * @param aValue  the value to test
 * @return the type assertion
 */
function _isSingleReferenceElement(aValue: any): aValue is SingleReferenceElement {
    return _isReferenceElement(aValue) && isNotNil(aValue[_PROP_VALUE]);
}

/*
 * Tests if an element is a reference element
 *
 * @param aValue  the value to test
 * @return the type assertion
 */
function _isMultiReferenceElement(aValue: any): aValue is MultiReferenceElement {
    return _isReferenceElement(aValue) && isArray(aValue[_PROP_VALUES]);
}

/*
 * Tests if an element is a location element
 *
 * @param aValue  the value to test
 * @return the type assertion
 */
function _isLocationElement(aValue: any): aValue is LocationElement {
    return _isType(ELEMENT_TYPE_LOCATION, aValue);
}

function _isSomeDateElement(aValue: any): aValue is SingleDateElement | MultiDateElement {
    return _isSingleDateElement(aValue) || _isMultiDateElement(aValue);
}

function _isSomeFileElement(aValue: any): aValue is SingleFileElement | MultiFileElement {
    return _isSingleFileElement(aValue) || _isMultiFileElement(aValue);
}

function _isSomeFormattedTextElement(aValue: any): aValue is SingleFormattedTextElement | MultiFormattedTextElement {
    return _isSingleFormattedTextElement(aValue) || _isMultiFormattedTextElement(aValue);
}

function _isSomeGroupElement(aValue: any): aValue is SingleGroupElement | MultiGroupElement {
    return _isSingleGroupElement(aValue) || _isMultiGroupElement(aValue);
}

function _isSomeImageElement(aValue: any): aValue is SingleImageElement | MultiImageElement {
    return _isSingleImageElement(aValue) || _isMultiImageElement(aValue);
}

function _isSomeLinkElement(aValue: any): aValue is SingleLinkElement | MultiLinkElement {
    return _isSingleLinkElement(aValue) || _isMultiLinkElement(aValue);
}

function _isSomeNumberElement(aValue: any): aValue is SingleNumberElement | MultiNumberElement {
    return _isSingleNumberElement(aValue) || _isMultiNumberElement(aValue);
}

function _isSomeOptionSelectionElement(aValue: any): aValue is SingleOptionSelectionElement | MultiOptionSelectionElement {
    return _isSingleOptionSelectionElement(aValue) || _isMultiOptionSelectionElement(aValue);
}

function _isSomeReferenceElement(aValue: any): aValue is SingleReferenceElement | MultiReferenceElement {
    return _isSingleReferenceElement(aValue) || _isMultiReferenceElement(aValue);
}

function _isSomeTextElement(aValue: any): aValue is SingleTextElement | MultiTextElement {
    return _isSingleTextElement(aValue) || _isMultiTextElement(aValue);
}

function _isSomeToggleElement(aValue: any): aValue is SingleToggleElement | MultiToggleElement {
    return _isSingleToggleElement(aValue) || _isMultiToggleElement(aValue);
}

function _isSomeVideoElement(aValue: any): aValue is SingleVideoElement | MultiVideoElement {
    return _isSingleVideoElement(aValue) || _isMultiVideoElement(aValue);
}

/**
 * Maps from element type to handler function
 */
const HANDLERS: ElementMap<Predicate<any>> = {
    [ELEMENT_TYPE_CATEGORY]: _isCategoryElement,
    [ELEMENT_TYPE_DATE]: _isSomeDateElement,
    [ELEMENT_TYPE_FILE]: _isSomeFileElement,
    [ELEMENT_TYPE_FORMATTED_TEXT]: _isSomeFormattedTextElement,
    [ELEMENT_TYPE_GROUP]: _isSomeGroupElement,
    [ELEMENT_TYPE_IMAGE]: _isSomeImageElement,
    [ELEMENT_TYPE_LINK]: _isSomeLinkElement,
    [ELEMENT_TYPE_LOCATION]: _isLocationElement,
    [ELEMENT_TYPE_NUMBER]: _isSomeNumberElement,
    [ELEMENT_TYPE_OPTION_SELECTION]: _isSomeOptionSelectionElement,
    [ELEMENT_TYPE_REFERENCE]: _isSomeReferenceElement,
    [ELEMENT_TYPE_TEXT]: _isSomeTextElement,
    [ELEMENT_TYPE_TOGGLE]: _isSomeToggleElement,
    [ELEMENT_TYPE_VIDEO]: _isSomeVideoElement
}

const _falseHandler = () => false;

const  _getHandler= (aElementType: string): Predicate<any> => HANDLERS[aElementType] || _falseHandler;

/**
 * Tests if the value is a value element
 *
 * @param aValue    the value
 * @return true if the value is a valid element
 */
function _isElement(aValue: any): aValue is Element {
    return isPlainObject(aValue) && isString(aValue[_PROP_TYPE]) && _getHandler(aValue[_PROP_TYPE])(aValue);
}

export {
    // typechecks
    _isOptionSelectionElement as isOptionSelectionElement,
    _isCategoryElement as isCategoryElement,
    _isDateElement as isDateElement,
    _isLinkElement as isLinkElement,
    _isTextElement as isTextElement,
    _isSingleTextElement as isSingleTextElement,
    _isMultiTextElement as isMultiTextElement,
    _isGroupElement as isGroupElement,
    _isSingleGroupElement as isSingleGroupElement,
    _isMultiGroupElement as isMultiGroupElement,
    _isFormattedTextElement as isFormattedTextElement,
    _isSingleFormattedTextElement as isSingleFormattedTextElement,
    _isMultiFormattedTextElement as isMultiFormattedTextElement,
    _isNumberElement as isNumberElement,
    _isSingleNumberElement as isSingleNumberElement,
    _isMultiNumberElement as isMultiNumberElement,
    _isImageElement as isImageElement,
    _isFileElement as isFileElement,
    _isVideoElement as isVideoElement,
    _isToggleElement as isToggleElement,
    _isSingleToggleElement as isSingleToggleElement,
    _isMultiToggleElement as isMultiToggleElement,
    _isReferenceElement as isReferenceElement,
    _isSingleReferenceElement as isSingleReferenceElement,
    _isMultiReferenceElement as isMultiReferenceElement,
    _isLocationElement as isLocationElement,
    _isSingleDateElement as isSingleDateElement,
    _isMultiDateElement as isMultiDateElement,
    _isSingleFileElement as isSingleFileElement,
    _isMultiFileElement as isMultiFileElement,
    _isSingleImageElement as isSingleImageElement,
    _isMultiImageElement as isMultiImageElement,
    _isSingleLinkElement as isSingleLinkElement,
    _isMultiLinkElement as isMultiLinkElement,
    _isSingleVideoElement as isSingleVideoElement,
    _isMultiVideoElement as isMultiVideoElement,
    _isSingleOptionSelectionElement as isSingleOptionSelectionElement,
    _isMultiOptionSelectionElement as isMultiOptionSelectionElement,
    _isElement as isElement
};
